/**
 * Core data model for a guidebook project.
 *
 * Section 9 rule: the project document stores the namespace once and every
 * object as a BARE name-part only. Full `namespace:name` identifiers, lang-key
 * roots, and texture short-names are composed at export time (see
 * `model/identifiers.ts`). Never persist a composed string in this document.
 */

export const PROJECT_FORMAT_VERSION = 1;

/** A language present in the project. `code` is a Bedrock locale, e.g. "en_US". */
export interface Language {
  code: string;
  /** Human label shown in the editor, e.g. "English (US)". */
  label: string;
}

/**
 * A reference to an item used inside a recipe. We store the KIND explicitly so
 * that renaming the namespace re-derives every project reference in one pass,
 * while vanilla `minecraft:*` references pass through untouched.
 *
 * `tag: true` means the id names an ITEM TAG rather than a single item, i.e.
 * "any item carrying this tag" (`{"tag": "minecraft:planks"}` in recipe JSON,
 * which is how vanilla expresses "any planks"). The kind axis is unchanged by
 * this: a project tag is still stored bare so a namespace rename re-derives it.
 * Tags are only meaningful as INPUTS; a recipe result must always be an item.
 */
export type ItemRef =
  | { kind: "project"; id: string; tag?: boolean } // id is a bare name-part in the project namespace
  | { kind: "vanilla"; id: string; tag?: boolean }; // id is a full id e.g. "minecraft:book"

/** A custom item produced/edited in the project. `id` is a bare name-part. */
export interface Item {
  uuid: string; // internal only, never exported
  id: string; // name-part, e.g. "tin_can"
  /** Display name per language code -> string. Exported as `item.NS:<id>`. */
  displayName: Record<string, string>;
  /** Vanilla menu category the item appears under in the creative inventory. */
  category: "construction" | "equipment" | "items" | "nature" | "none";
  /** Optional inline PNG (data URL) for the item's texture. */
  textureDataUrl?: string;
}

export type RecipeShape = "shapeless" | "shaped";

export interface Recipe {
  uuid: string; // internal only
  id: string; // name-part, unique among recipes
  shape: RecipeShape;
  /** Crafting station tags, e.g. ["crafting_table"]. */
  tags: string[];
  /** Shapeless ingredient list (used when shape === "shapeless"). */
  ingredients: ItemRef[];
  /**
   * Shaped recipe grid. `pattern` is up to 3 rows of up to 3 chars; `key` maps
   * a pattern char to an ItemRef. Used when shape === "shaped".
   */
  pattern: string[];
  key: Record<string, ItemRef>;
  /** The crafted result. */
  result: ItemRef;
  resultCount: number;
}

/**
 * A single entry ("item") shown under a category. In-game, a category button
 * opens a list of entries; clicking an entry shows its title + body. Entries
 * map to lang keys `<ns>.guide.<categoryId>.<id>.title` / `.body`.
 */
export interface GuideEntry {
  uuid: string;
  /** Key segment, e.g. "bison". Composed into the entry's lang keys. */
  id: string;
  /** Per-language entry title (the button label + page heading). */
  title: Record<string, string>;
  /** Per-language entry body text. */
  body: Record<string, string>;
  /**
   * Optional recipe to display on this entry, referenced by the recipe's bare
   * name-part id. Renders the ingredients/result in the generated script UI.
   */
  recipeId?: string;
}

/**
 * A guidebook category. On the homepage each category is a button; opening it
 * lists the category's entries. Maps to `<ns>.guide.<id>.title` / `.body`.
 */
export interface GuideCategory {
  uuid: string;
  /** Key segment, e.g. "animals". */
  id: string;
  /** Per-language category title (the homepage button label + submenu title). */
  title: Record<string, string>;
  /** Per-language category intro/body shown above its entry list. */
  body: Record<string, string>;
  entries: GuideEntry[];
}

export interface GuideConfig {
  /** Per-language book title. Exported as `item.NS:<guideItemId>` and the home title. */
  title: Record<string, string>;
  /** Per-language homepage intro text (`<ns>.guide.main.body`). */
  intro: Record<string, string>;
  /** Give the guide book to each player once, on their first spawn. */
  giveOnFirstJoin: boolean;
  categories: GuideCategory[];
}

/**
 * Manifest data. These values are NOT namespace-derived: they use UUIDs and
 * are left alone when the namespace changes (Section 9.3).
 */
export interface ManifestConfig {
  behaviorHeaderUuid: string;
  behaviorModuleUuid: string;
  behaviorScriptUuid: string;
  resourceHeaderUuid: string;
  resourceModuleUuid: string;
  version: [number, number, number];
  minEngineVersion: [number, number, number];
  /** Script API module versions used by the generated main.js. */
  serverModuleVersion: string;
  serverUiModuleVersion: string;
}

export interface ProjectMeta {
  name: string;
  description: string;
  author: string;
}

export interface Project {
  formatVersion: number;

  meta: ProjectMeta;

  /** THE single source of truth for the namespace (Section 9.2). */
  namespace: string;

  /** Name-part of the guide item, e.g. "guide" -> NS:guide. */
  guideItemId: string;

  languages: Language[];

  items: Item[];
  recipes: Recipe[];
  guide: GuideConfig;

  manifest: ManifestConfig;

  /**
   * Raw `.lang` file contents captured at import time, keyed by locale. When
   * present for a locale, export MERGES the managed keys into this base
   * (overriding managed keys, preserving every other line) instead of writing a
   * fresh file, so importing an add-on never destroys its existing
   * translations (block/entity/UI names, etc.) that this tool doesn't model.
   */
  importedLang?: Record<string, string>;
}
